import { Head, Link } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";

import { ActivationGuide } from "@/components/activation-guide";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface OgImageApiFaq {
  question: string;
  answer: string;
}

interface OgImageApiProps {
  page_title?: string;
  faqs?: OgImageApiFaq[];
}

const NEXT_IMAGE_RESPONSE_SAMPLE = `import { ImageResponse } from 'next/og'
// App Router includes @vercel/og — no separate install required.

export async function GET() {
  return new ImageResponse(
    (
      <div
        style={{
          fontSize: 48,
          color: '#0f172a',
          background: '#fff7ed',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 64,
        }}
      >
        Hello from ImageResponse
      </div>
    ),
    { width: 1200, height: 630 },
  )
}`;

const OG_PILOT_JS_SAMPLE = `import { configure, createImage } from "og-pilot-js"

configure((config) => {
  config.apiKey = process.env.OG_PILOT_API_KEY
  config.domain = process.env.OG_PILOT_DOMAIN
})

export async function blogOgImage(post: {
  title: string
  description: string
  path: string
  imageUrl?: string
}) {
  return createImage(
    {
      template: "blog_post",
      title: post.title,
      description: post.description,
      image_url: post.imageUrl,
      path: post.path,
    },
    { iat: Math.floor(Date.now() / 1000) },
  )
}

// Next.js metadata example
export async function generateMetadata({ params }) {
  const post = await getPost(params.slug)
  const ogImage = await blogOgImage({
    title: post.title,
    description: post.excerpt,
    path: \`/blog/\${post.slug}\`,
  })
  return {
    openGraph: { images: [ogImage] },
    twitter: { card: "summary_large_image", images: [ogImage] },
  }
}`;

const RAW_HTTP_SAMPLE = `POST https://ogpilot.com/api/v1/images
Content-Type: application/json

{"token":"YOUR_JWT_TOKEN"}`;

const MCP_POINTER_SAMPLE = `Remote MCP URL: https://ogpilot.com/mcp
Docs: https://ogpilot.com/docs/mcp
OAuth: follow the custom connector guide on /docs/mcp (OAuth 2.1 + PKCE)`;

const sectionClass =
  "mt-20 rounded-3xl border border-orange-200/80 bg-white/90 p-8 shadow-xl dark:border-orange-500/25 dark:bg-slate-900/80 dark:shadow-none";

const linkClass = "font-medium text-orange-700 hover:underline dark:text-orange-300";

function CodeSample({ label, code }: { label: string; code: string }) {
  return (
    <div className="mt-6">
      <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</p>
      <pre className="overflow-x-auto rounded-2xl border border-orange-200 bg-orange-50 p-5 text-sm leading-relaxed text-slate-900 dark:border-orange-500/30 dark:bg-slate-950 dark:text-orange-50">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function OgImageApi({
  page_title: pageTitle = "OG Image API vs @vercel/og vs DIY",
  faqs = [],
}: OgImageApiProps) {
  return (
    <>
      <Head title={pageTitle} />

      <div className="min-h-screen bg-[radial-gradient(circle_at_0%_10%,#fed7aa_0,transparent_45%),radial-gradient(circle_at_100%_0%,#fecaca_0,transparent_38%),linear-gradient(180deg,#fff7ed_0%,#ffffff_32%,#fffbeb_100%)] pb-20 dark:bg-[radial-gradient(circle_at_0%_10%,#431407_0,transparent_45%),radial-gradient(circle_at_100%_0%,#7f1d1d_0,transparent_38%),linear-gradient(180deg,#0f172a_0%,#111827_32%,#0b1120_100%)]">
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <PublicHeader />

          <section className="relative mt-8 overflow-hidden rounded-[2.25rem] border border-orange-200/80 bg-white/85 p-8 shadow-[0_35px_70px_-40px_rgba(194,65,12,0.55)] backdrop-blur sm:p-10 dark:border-orange-500/30 dark:bg-slate-900/80 dark:shadow-[0_35px_70px_-40px_rgba(249,115,22,0.35)]">
            <div className="pointer-events-none absolute -top-16 -right-10 h-48 w-48 rounded-full bg-red-300/35 blur-3xl dark:bg-red-500/20" />
            <div className="pointer-events-none absolute -bottom-20 -left-10 h-52 w-52 rounded-full bg-orange-300/40 blur-3xl dark:bg-orange-500/20" />

            <div className="relative">
              <Badge className="mb-5 border border-orange-200 bg-orange-100 px-4 py-1.5 tracking-[0.22em] text-orange-700 uppercase dark:border-orange-500/40 dark:bg-orange-500/20 dark:text-orange-100">
                OG image API comparison
              </Badge>

              <h1 className="max-w-4xl text-4xl leading-tight font-bold text-balance text-slate-900 sm:text-5xl lg:text-6xl dark:text-slate-100">
                Hosted OG image API vs @vercel/og vs DIY Satori
              </h1>

              <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-700 sm:text-lg dark:text-slate-300">
                Choosing an <strong>OG image API</strong> (or rolling your own) decides how every link preview looks on
                Slack, X, LinkedIn, and iMessage. This guide compares three real paths teams use in 2026: a{" "}
                <strong>hosted OG image generator API</strong> (OG Pilot),{" "}
                <strong>@vercel/og / Next.js ImageResponse</strong>, and <strong>DIY Satori</strong>. No invented
                benchmarks—capabilities below cite official docs; anything unverified is marked.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button asChild size="lg" className="bg-orange-500 text-white hover:bg-orange-600">
                  <Link href="/sign_up">
                    Get API key
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <div className="grid grid-cols-2 gap-3 sm:flex">
                  <Button asChild size="lg" variant="outline">
                    <Link href="/docs">Read API docs</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline">
                    <Link href="/docs/mcp">Use MCP</Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Who this is for</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Developers and product teams deciding how to generate Open Graph images for:
            </p>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-slate-700 dark:text-slate-300">
              <li>Next.js apps already on Vercel</li>
              <li>Multi-framework stacks (Rails, Astro, WordPress, static sites)</li>
              <li>Agent / MCP workflows that should create social images from a URL</li>
            </ul>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              If you only need one static card, design it once and ship a PNG. If every route needs a branded preview,
              you need an <strong>og image generator API</strong> or an in-app renderer.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              Comparison matrix: hosted API vs @vercel/og vs DIY Satori
            </h2>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[52rem] border-collapse text-left text-sm text-slate-700 dark:text-slate-300">
                <thead>
                  <tr className="border-b border-orange-200 dark:border-orange-500/30">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Dimension</th>
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Hosted OG Pilot API</th>
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">
                      @vercel/og / ImageResponse
                    </th>
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">DIY Satori</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-orange-100 align-top dark:border-orange-500/20">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Where it runs</th>
                    <td className="px-3 py-3">
                      Hosted API + CDN URL you put in <code>og:image</code>
                    </td>
                    <td className="px-3 py-3">Your Next.js/Vercel function (or compatible host)</td>
                    <td className="px-3 py-3">Your own worker/server + CDN</td>
                  </tr>
                  <tr className="border-b border-orange-100 align-top dark:border-orange-500/20">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">
                      How you author layouts
                    </th>
                    <td className="px-3 py-3">
                      Ready templates (<code>page</code>, <code>blog_post</code>, <code>product</code>, …) + JWT params
                    </td>
                    <td className="px-3 py-3">
                      JSX + limited CSS via <code>ImageResponse</code>
                    </td>
                    <td className="px-3 py-3">JSX/HTML subset via Satori; you wire PNG/SVG pipeline</td>
                  </tr>
                  <tr className="border-b border-orange-100 align-top dark:border-orange-500/20">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Auth model</th>
                    <td className="px-3 py-3">
                      Sign JWT locally with API key; key never leaves your server (
                      <Link href="/docs" className={linkClass}>
                        docs
                      </Link>
                      )
                    </td>
                    <td className="px-3 py-3">App-owned route; you secure query params yourself</td>
                    <td className="px-3 py-3">You own auth, abuse controls, and signing</td>
                  </tr>
                  <tr className="border-b border-orange-100 align-top dark:border-orange-500/20">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Framework fit</th>
                    <td className="px-3 py-3">JS, Ruby, Python, PHP SDKs + WordPress plugin + MCP</td>
                    <td className="px-3 py-3">
                      Best for Next.js App Router (<code>next/og</code>); <code>@vercel/og</code> elsewhere
                    </td>
                    <td className="px-3 py-3">Any stack that can host a renderer</td>
                  </tr>
                  <tr className="border-b border-orange-100 align-top dark:border-orange-500/20">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">CSS / layout limits</th>
                    <td className="px-3 py-3">Template system (no per-request CSS authoring)</td>
                    <td className="px-3 py-3">
                      Flexbox + subset of CSS; <strong>no display: grid</strong>; max <strong>500KB</strong> bundle
                      incl. fonts/assets (
                      <a
                        href="https://nextjs.org/docs/app/api-reference/functions/image-response"
                        className={linkClass}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Next.js ImageResponse
                      </a>
                      ,{" "}
                      <a
                        href="https://vercel.com/docs/og-image-generation"
                        className={linkClass}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Vercel OG
                      </a>
                      )
                    </td>
                    <td className="px-3 py-3">Same Satori CSS subset unless you change engines</td>
                  </tr>
                  <tr className="border-b border-orange-100 align-top dark:border-orange-500/20">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Fonts</th>
                    <td className="px-3 py-3">Handled by templates / product</td>
                    <td className="px-3 py-3">
                      <code>ttf</code> / <code>otf</code> / <code>woff</code> (prefer ttf/otf) per Vercel/Next docs
                    </td>
                    <td className="px-3 py-3">You load and subset fonts</td>
                  </tr>
                  <tr className="border-b border-orange-100 align-top dark:border-orange-500/20">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Caching</th>
                    <td className="px-3 py-3">
                      CDN URLs; optional <code>iat</code> for daily bust (
                      <Link href="/docs" className={linkClass}>
                        docs
                      </Link>
                      )
                    </td>
                    <td className="px-3 py-3">
                      <code>@vercel/og</code> sets cache headers for CDN reuse (
                      <a
                        href="https://vercel.com/docs/og-image-generation"
                        className={linkClass}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Vercel
                      </a>
                      )
                    </td>
                    <td className="px-3 py-3">You configure CDN + cache keys</td>
                  </tr>
                  <tr className="border-b border-orange-100 align-top dark:border-orange-500/20">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Agent / MCP</th>
                    <td className="px-3 py-3">
                      Public MCP at <code>https://ogpilot.com/mcp</code> (
                      <Link href="/docs/mcp" className={linkClass}>
                        docs/mcp
                      </Link>
                      )
                    </td>
                    <td className="px-3 py-3">Not productized as MCP; you’d wrap your route</td>
                    <td className="px-3 py-3">Build your own tool surface</td>
                  </tr>
                  <tr className="border-b border-orange-100 align-top dark:border-orange-500/20">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Ops burden</th>
                    <td className="px-3 py-3">Domain verify + API key; rendering ops hosted</td>
                    <td className="px-3 py-3">Maintain route, fonts, payload size, runtime limits</td>
                    <td className="px-3 py-3">Full: Satori + Resvg (or similar), fonts, CDN, monitoring, abuse</td>
                  </tr>
                  <tr className="border-b border-orange-100 align-top dark:border-orange-500/20">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">
                      Latency (p50 cold/warm)
                    </th>
                    <td className="px-3 py-3">NEED VERIFY — measure against your domains and templates</td>
                    <td className="px-3 py-3">NEED VERIFY — depends on function region, fonts, and asset fetches</td>
                    <td className="px-3 py-3">NEED VERIFY — depends on your infra</td>
                  </tr>
                  <tr className="align-top">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Cost model</th>
                    <td className="px-3 py-3">
                      Account credits / subscription (see in-app billing after signup) — NEED CITE for public list
                      prices
                    </td>
                    <td className="px-3 py-3">
                      Function + bandwidth on your host; image compute billed as your platform bills edge/serverless —
                      NEED CITE for your plan
                    </td>
                    <td className="px-3 py-3">Infra + engineering time — NEED CITE</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              <strong>Recommended OG size:</strong> 1200×630 is the size Vercel documents as the recommended OG image
              size (
              <a
                href="https://vercel.com/docs/og-image-generation"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Vercel OG Image Generation
              </a>
              ).
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              When to pick hosted API, @vercel/og, or DIY
            </h2>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              <article className="rounded-2xl border border-orange-100 bg-orange-50/70 p-5 dark:border-orange-500/20 dark:bg-slate-950/60">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Pick a hosted OG image API (OG Pilot) when:
                </h3>
                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-slate-700 dark:text-slate-300">
                  <li>You need the same branded cards across Next.js, Rails, Astro, WordPress, or no-code pages</li>
                  <li>You want JWT-signed URLs and SDKs instead of maintaining a renderer</li>
                  <li>
                    You want MCP tools (<code>generate_og_image</code>, <code>debug_open_graph</code>, …) for agent
                    workflows (
                    <Link href="/docs" className={linkClass}>
                      docs
                    </Link>
                    )
                  </li>
                  <li>Editors should not own Edge Function CSS</li>
                </ul>
              </article>
              <article className="rounded-2xl border border-orange-100 bg-orange-50/70 p-5 dark:border-orange-500/20 dark:bg-slate-950/60">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Pick @vercel/og / ImageResponse when:
                </h3>
                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-slate-700 dark:text-slate-300">
                  <li>You are deep in Next.js App Router and want layouts as JSX next to the page</li>
                  <li>
                    You accept Satori CSS limits and the 500KB bundle cap (
                    <a
                      href="https://nextjs.org/docs/app/api-reference/functions/image-response"
                      className={linkClass}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Next.js docs
                    </a>
                    )
                  </li>
                  <li>One deployables-with-the-app pipeline is enough; no multi-CMS requirement</li>
                </ul>
              </article>
              <article className="rounded-2xl border border-orange-100 bg-orange-50/70 p-5 dark:border-orange-500/20 dark:bg-slate-950/60">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Pick DIY Satori when:</h3>
                <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-6 text-slate-700 dark:text-slate-300">
                  <li>You need a custom rendering pipeline or non-Vercel runtime with full ownership</li>
                  <li>You already operate image workers and want zero SaaS dependency</li>
                  <li>
                    You will maintain fonts, PNG conversion (e.g. Resvg), caching, and abuse protection yourself (
                    <a href="https://github.com/vercel/satori" className={linkClass} target="_blank" rel="noreferrer">
                      Satori
                    </a>
                    )
                  </li>
                </ul>
              </article>
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              How Next.js ImageResponse and @vercel/og work
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Official Next.js docs: import <code>ImageResponse</code> from <code>next/og</code>. It uses{" "}
              <code>@vercel/og</code>, Satori, and Resvg to turn JSX into PNG. Default size is 1200×630. Only flexbox
              and a CSS subset are supported; advanced layouts like <code>display: grid</code> will not work. Bundle
              size (JSX, CSS, fonts, images) must stay under 500KB. Font formats: <code>ttf</code>, <code>otf</code>,{" "}
              <code>woff</code>.
            </p>
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Sources:{" "}
              <a
                href="https://nextjs.org/docs/app/api-reference/functions/image-response"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Next.js ImageResponse
              </a>
              ,{" "}
              <a
                href="https://vercel.com/docs/og-image-generation"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Vercel OG Image Generation
              </a>
              ,{" "}
              <a
                href="https://vercel.com/docs/og-image-generation/og-image-api"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Vercel OG image API
              </a>
              .
            </p>
            <CodeSample label="Code sample — Next.js App Router route" code={NEXT_IMAGE_RESPONSE_SAMPLE} />
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Runtime note from Vercel: Pages Router + Node.js runtime does <strong>not</strong> support{" "}
              <code>return new Response(…)</code> with <code>vercel/og</code>; App Router and Edge combinations listed
              in their matrix are supported (
              <a
                href="https://vercel.com/docs/og-image-generation"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Vercel OG docs
              </a>
              ).
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              How the OG Pilot OG image API works
            </h2>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-base leading-7 text-slate-700 dark:text-slate-300">
              <li>
                Sign up and verify your domain (DNS TXT) —{" "}
                <Link href="/sign_up" className={linkClass}>
                  https://ogpilot.com/sign_up
                </Link>
              </li>
              <li>Create an API key in Settings → API Keys</li>
              <li>
                Sign a JWT locally (HS256) with claims like <code>iss</code> (domain), <code>sub</code> (API key
                prefix), <code>template</code>, <code>title</code>, …
              </li>
              <li>
                <code>POST https://ogpilot.com/api/v1/images</code> with <code>{`{"token":"<jwt>"}`}</code> — response
                is a CDN image URL for <code>og:image</code>
              </li>
              <li>
                Or use SDKs: <code>og-pilot-js</code>, <code>og_pilot_ruby</code>, <code>og-pilot</code> (Python),{" "}
                <code>og-pilot-php</code>
              </li>
            </ol>
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Source:{" "}
              <Link href="/docs" className={linkClass}>
                https://ogpilot.com/docs
              </Link>
            </p>
            <CodeSample label="Code sample — JavaScript SDK (server-side only)" code={OG_PILOT_JS_SAMPLE} />
            <CodeSample label="Raw HTTP shape (from docs)" code={RAW_HTTP_SAMPLE} />
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Never hardcode the API key in the browser. Sign on the server.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              MCP: generate OG images from an agent
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              OG Pilot exposes a remote MCP endpoint at <code>https://ogpilot.com/mcp</code>. Documented tools include{" "}
              <code>generate_og_image</code>, <code>debug_open_graph</code>, <code>compress_image</code>, and{" "}
              <code>extract_color_palette</code>. Generation takes a page URL and returns an Open Graph image. Debug /
              compress / palette do not spend credits. Setup guide:{" "}
              <Link href="/docs/mcp" className={linkClass}>
                https://ogpilot.com/docs/mcp
              </Link>
            </p>
            <p className="mt-4 text-base leading-7 text-slate-700 dark:text-slate-300">
              <Link href="/docs/mcp" className={linkClass}>
                Connect MCP
              </Link>{" "}
              ·{" "}
              <Link href="/open-graph-debugger" className={linkClass}>
                Validate a URL
              </Link>
            </p>
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Copy-paste pointer for assistants (keep accurate to product docs; if exact Claude/ChatGPT JSON config
              differs by client, link{" "}
              <Link href="/docs/mcp" className={linkClass}>
                /docs/mcp
              </Link>{" "}
              rather than inventing a connector JSON):
            </p>
            <CodeSample label="MCP endpoint pointer" code={MCP_POINTER_SAMPLE} />
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">What “DIY Satori” actually means</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              <a href="https://github.com/vercel/satori" className={linkClass} target="_blank" rel="noreferrer">
                Satori
              </a>{" "}
              converts a limited HTML/CSS tree to SVG. <code>@vercel/og</code> already wraps Satori + Resvg for PNG. A
              DIY path means you choose:
            </p>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-slate-700 dark:text-slate-300">
              <li>Where Satori runs (Node worker, edge-compatible runtime, container)</li>
              <li>How you convert SVG → PNG (Resvg or another engine)</li>
              <li>Font loading / subsetting</li>
              <li>URL design, signing, rate limits, and CDN cache keys</li>
              <li>Monitoring when social crawlers hammer a popular post</li>
            </ul>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              That flexibility is real. So is the ongoing ops cost. NEED VERIFY any claim that DIY is “always cheaper”
              than hosted or Vercel Functions—it depends on volume, eng time, and failure modes.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              Latency and cost — what we will not invent
            </h2>
            <ul className="mt-4 list-disc space-y-3 pl-5 text-base leading-7 text-slate-700 dark:text-slate-300">
              <li>
                <strong>Cold vs warm latency:</strong> NEED VERIFY with your own timings for OG Pilot CDN hits, Vercel
                Function regions, and DIY workers. Social crawlers also cache aggressively; first-fetch latency ≠ what
                users see after a platform cache is warm.
              </li>
              <li>
                <strong>Cost per 1,000 images:</strong> NEED CITE from your host’s function/bandwidth pricing and from
                OG Pilot’s in-app plan after signup. Do not publish a round number here without a source.
              </li>
              <li>
                <strong>Engineering cost:</strong> DIY and heavy custom <code>@vercel/og</code> layouts trade SaaS fees
                for design-system maintenance inside Edge bundles (watch the 500KB cap).
              </li>
            </ul>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Use{" "}
              <Link href="/open-graph-debugger" className={linkClass}>
                /open-graph-debugger
              </Link>{" "}
              after deploy to confirm crawlers see the URL you intended.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Decision checklist</h2>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-base leading-7 text-slate-700 dark:text-slate-300">
              <li>
                One framework on Vercel with JSX layouts you control? Start with <code>ImageResponse</code>.
              </li>
              <li>Multiple apps/CMS and one brand system? Prefer a hosted OG image API.</li>
              <li>
                Need agents to generate/debug cards? Prefer a product with MCP (
                <Link href="/docs/mcp" className={linkClass}>
                  /docs/mcp
                </Link>
                ).
              </li>
              <li>Extreme custom rendering and you already run image infra? DIY Satori can win—budget for ops.</li>
              <li>
                Always put absolute <code>og:image</code> URLs in HTML and re-scrape after changes.
              </li>
            </ol>
          </section>

          <ActivationGuide
            title="Ship the API path that matches your stack"
            links={[
              {
                href: "/sign_up",
                label: "Get an API key",
                detail: "Create an account, verify a domain, and issue a signing key.",
              },
              {
                href: "/docs",
                label: "API documentation",
                detail: "JWT claims, templates, and SDK install steps.",
              },
              {
                href: "/docs/mcp",
                label: "MCP connector",
                detail: "Remote tools for generate, debug, compress, palette.",
              },
              {
                href: "/open-graph-debugger",
                label: "Open Graph debugger",
                detail: "Confirm Facebook, X, LinkedIn, Slack, and WhatsApp see the right card.",
              },
              {
                href: "/new",
                label: "Generate one image",
                detail: "One-off branded preview without wiring production auth yet.",
              },
              {
                href: "/nextjs-og-image-generator",
                label: "Next.js OG image",
                detail: "Metadata file vs ImageResponse vs a hosted API, for a single Next.js app.",
              },
              {
                href: "/change-og-image-wordpress",
                label: "Change the WordPress OG image",
                detail: "Use a generated card when a featured image or SEO-plugin upload is the wrong fix.",
              },
            ]}
          >
            <p>
              Compare the matrix above, then activate: create an API key for signed CDN URLs, or connect MCP for
              agent-driven generation. If you are standardizing Next.js metadata only, keep <code>ImageResponse</code>
              —and still use the debugger before you trust a share. When brand templates must match across Rails,
              WordPress, and Next, one hosted OG image generator API keeps layouts from drifting.
            </p>
          </ActivationGuide>

          {faqs.length > 0 ? (
            <section className={sectionClass}>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">OG image API questions</h2>
              <div className="mt-6 space-y-6">
                {faqs.map((faq) => (
                  <article key={faq.question}>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{faq.question}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{faq.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <section className="mt-20 rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-orange-900 p-10 text-white shadow-xl">
            <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
              <div>
                <h2 className="text-3xl font-bold sm:text-4xl">Get an API key or connect MCP</h2>
                <p className="mt-4 max-w-xl text-sm leading-6 text-slate-200">
                  Create a signed OG image generator API workflow in minutes—or keep <code>@vercel/og</code> for a
                  single Next.js app and use the debugger before you ship.
                </p>
              </div>
              <div className="rounded-2xl border border-white/20 bg-white/10 p-6">
                <div className="flex flex-col gap-3">
                  <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                    <Link href="/sign_up">
                      Get API key
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button
                      asChild
                      variant="outline"
                      className="border-white/40 bg-transparent text-white hover:bg-white/10"
                    >
                      <Link href="/docs">API docs</Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="border-white/40 bg-transparent text-white hover:bg-white/10"
                    >
                      <Link href="/docs/mcp">Use MCP</Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <SiteFooter className="mt-16" showSeparator={false} />
        </div>
      </div>
    </>
  );
}
